// Lightweight enough for the shared logout boundary: no Vue, editor or network.
export const FORUM_DRAFT_PREFIX = 'eurekers:forum-draft:v1:';
const OWNER = FORUM_DRAFT_PREFIX + 'owner';
const DAY = 24 * 60 * 60 * 1000;
const instances = new Set();
// Memory-only session identity: never persist access/session tokens. Multiple
// mounted editors must not let an old callback erase a newer account's drafts.
const storageOwners = new WeakMap();
const copy = value => JSON.parse(JSON.stringify(value));
const id = value => (typeof value === 'string' || Number.isSafeInteger(value)) && /^[1-9]\d*$/.test(String(value)) ? String(value) : null;
const browserStorage = () => window.sessionStorage;

function removeNamespace(getStorage) {
  try {
    const storage = getStorage(), keys = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key && key.startsWith(FORUM_DRAFT_PREFIX)) keys.push(key);
    }
    keys.forEach(key => storage.removeItem(key));
    return true;
  } catch (_) { return false; }
}

/** Called after confirmed logout or session expiry, even outside forum routes. */
export function clearForumDrafts() {
  removeNamespace(browserStorage);
  instances.forEach(instance => instance.clear());
}

function contextParts(context) {
  const { mode } = context || {};
  if (mode === 'create') {
    if (context.sectionId != null && !id(context.sectionId)) throw new Error('Sección de borrador no válida.');
    return [mode, context.sectionId == null ? null : id(context.sectionId)];
  }
  if (!id(context.threadId)) throw new Error('Hilo de borrador no válido.');
  if (mode === 'reply') return [mode, id(context.threadId)];
  if (mode === 'edit' && ['topic', 'comment'].includes(context.postType) && id(context.postId)) return [mode, id(context.threadId), context.postType, id(context.postId)];
  throw new Error('Contexto de borrador no válido.');
}

function draftValue(value = {}) {
  return { title: typeof value.title === 'string' ? value.title : '', html: typeof value.html === 'string' ? value.html : '',
    sectionId: id(value.sectionId), quoteId: id(value.quoteId),
    attachmentIds: Array.isArray(value.attachmentIds) ? [...new Set(value.attachmentIds.map(id).filter(Boolean))] : [],
    // Persist pending sends with their draft so reload cannot silently turn an
    // uncertain publication into a fresh POST. This is not a session/CSRF token.
    ...(value.submission && typeof value.submission.requestId === 'string' && /^[a-zA-Z0-9_-]{16,80}$/.test(value.submission.requestId) ? { submission: { requestId: value.submission.requestId } } : {}) };
}

export function createForumDrafts({ getIdentity, getStorage = browserStorage, now = Date.now, ttl = DAY } = {}) {
  const memory = new Map();
  let liveIdentity = null, disposed = false, restoreBlocked = false, writesBlocked = false;
  function clear() {
    memory.clear(); restoreBlocked = !removeNamespace(getStorage); liveIdentity = null; writesBlocked = true;
    try { storageOwners.delete(getStorage()); } catch (_) { /* Storage may be blocked. */ }
  }
  const instance = { clear }; instances.add(instance);
  function owner(writing = false) {
    if (disposed) throw new Error('El almacén de borradores está cerrado.');
    const current = getIdentity() || {}, uid = id(current.uid), signature = JSON.stringify([uid, current.session || null]);
    if (!uid) { clear(); throw new Error('Inicia sesión para usar borradores.'); }
    if (liveIdentity && liveIdentity !== signature) {
      memory.clear(); writesBlocked = true; restoreBlocked = false;
    }
    // Only loading/reopening a scope acknowledges the new owner. Repeated saves
    // of an old buffer must remain blocked after logout or a session change.
    liveIdentity = signature;
    try {
      const storage = getStorage(), active = storageOwners.get(storage);
      if (active && active !== signature) { memory.clear(); restoreBlocked = !removeNamespace(getStorage); }
      storageOwners.set(storage, signature);
      if (!restoreBlocked && storage.getItem(OWNER) !== uid) { memory.clear(); restoreBlocked = !removeNamespace(getStorage); }
      storage.setItem(OWNER, uid); // UID only, never a session/access token.
    } catch (_) { restoreBlocked = true; }
    if (writing && writesBlocked) throw Object.assign(new Error('La sesión ha cambiado. Abre de nuevo el editor.'), { code: 'forum_session_changed' });
    if (!writing) writesBlocked = false;
    return uid;
  }
  const keyFor = (uid, context) => FORUM_DRAFT_PREFIX + JSON.stringify([uid, ...contextParts(context)]);
  const result = (record, persistence, extra = {}) => ({ draft: record ? copy(record.value) : null, updatedAt: record ? record.updatedAt : null, persistence, expired: false, ...extra });
  function load(context) {
    const uid = owner(), key = keyFor(uid, context);
    let record = memory.get(key), persistence = record ? record.persistence : 'session';
    if (!record && !restoreBlocked) {
      try { const raw = getStorage().getItem(key); record = raw ? JSON.parse(raw) : null; }
      catch (_) { return result(null, 'memory', { warning: 'No se pudo recuperar el borrador guardado.' }); }
    }
    if (!record) return result(null, restoreBlocked ? 'memory' : persistence);
    if (record.version !== 1 || record.uid !== uid || record.key !== key || !Number.isFinite(record.expiresAt)
        || !Number.isFinite(record.updatedAt) || !record.value || record.expiresAt <= now() || record.updatedAt > now()
        || record.expiresAt - record.updatedAt > ttl || record.expiresAt <= record.updatedAt) {
      memory.delete(key);
      try { getStorage().removeItem(key); } catch (_) { restoreBlocked = true; }
      return result(null, persistence, { expired: Number.isFinite(record.expiresAt) && record.expiresAt <= now() });
    }
    record.value = draftValue(record.value);
    memory.set(key, { ...record, persistence });
    return result(record, persistence);
  }
  function save(context, value) {
    const uid = owner(true), key = keyFor(uid, context);
    const record = { version: 1, uid, key, updatedAt: now(), expiresAt: now() + ttl, value: draftValue(value) };
    let persistence = 'session';
    try {
      if (restoreBlocked) throw new Error('Storage unavailable.');
      const storage = getStorage(), raw = JSON.stringify(record);
      let drafts = 0;
      for (let index = 0; index < storage.length; index++) { const candidate = storage.key(index); if (candidate !== OWNER && candidate && candidate.startsWith(FORUM_DRAFT_PREFIX)) drafts++; }
      // Never evict another unfinished draft to make room. Keep this one in
      // memory and let the composer explain its reduced persistence instead.
      if (raw.length > 250000 || (!storage.getItem(key) && drafts >= 20)) throw new Error('Draft capacity reached.');
      storage.setItem(key, raw);
    } catch (_) { persistence = 'memory'; }
    memory.set(key, { ...record, persistence });
    return result(record, persistence, persistence === 'memory' ? { warning: 'Este borrador solo está en memoria. No recargues ni cierres esta pestaña hasta copiarlo o publicarlo.' } : {});
  }
  function discard(context) {
    const key = keyFor(owner(true), context); memory.delete(key);
    try { getStorage().removeItem(key); return true; }
    catch (_) { restoreBlocked = true; return false; }
  }
  return { load, save, discard, clear, refreshIdentity() { try { owner(true); return true; } catch (_) { return false; } },
    dispose() { memory.clear(); disposed = true; instances.delete(instance); } };
}
